import React, { useMemo } from "react"
import Select, { SingleValue } from "react-select"
import {
  PropertyKey,
  PROPERTY_KEYS,
  GRADIENTS,
  RANGES,
} from "../model-properties"
import { AWSelect } from "aw-select"
import { useTheme } from "../../../utils/contexts/ThemeContext"
import { useTranslation } from "react-i18next"

type Option = { value: PropertyKey; label: string }

type ModelFilterProps = {
  selectedProperty: PropertyKey
  setSelectedProperty: React.Dispatch<React.SetStateAction<PropertyKey>>
}

const ModelFilter: React.FC<ModelFilterProps> = ({
  selectedProperty,
  setSelectedProperty,
}) => {
  const theme = useTheme()
  const dark = theme.theme === "dark"

  const { t } = useTranslation()

  const propertyOptions: Option[] = useMemo(
    () =>
      PROPERTY_KEYS.map((k) => ({
        value: k,
        label: t(k), 
      })),
    [t]
  )

  const selected =
    propertyOptions.find((opt) => opt.value === selectedProperty) ??
    propertyOptions[0]

  const gradientCSS = useMemo(() => {
    const stops = GRADIENTS[selected.value]
    return `linear-gradient(to right, ${stops
      .map((s) => `${s.color} ${s.offset * 100}%`)
      .join(", ")})`
  }, [selected.value])

  const [min, max] = RANGES[selected.value]

  const handleChange = (opt: SingleValue<Option>) => {
    setSelectedProperty((opt?.value as PropertyKey) ?? propertyOptions[0].value)
  }

  return (
    <div
      className="p-2 rounded-1 mb-2"
      style={{ backgroundColor: "var(--background-secondary)", boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }}
    >
      <div className="text-center">
        <span className="h3">{t("ModelOptions")}</span>
      </div>

      <div className="row m-0 py-2">
        <div className="col d-flex flex-column px-2">
          <label className="h5 mb-3" htmlFor="prop-select">
            {t("SelectProperties")}
          </label>

          <AWSelect
            className="mb-4"
            options={propertyOptions}
            value={selected}
            onChange={handleChange}
            clearable={false}
            dark={dark}
          />
        </div>

        <div className="col py-1 d-flex flex-column">
          <span className="h5 mb-2">{t(`${selected.value}Scale`)}</span>
          <div
            className="ModelGradient"
            style={{
              background: gradientCSS,
              width: "100%",
              height: "20px",
              borderRadius: "6px",
              boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
            }}
            aria-label={`${selected.label} color gradient`}
            role="img"
          />

          <div className="d-flex justify-content-between mt-1" aria-hidden="true">
            <small>{min}</small>
            <small>{max}</small>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModelFilter
